/**
 * `NumberField` (W1-12 ui-kit-i18n; contract NumberFieldProps, DESIGN.md §4.11, U§5 C3).
 *
 *  - Typing: a plain number (optionally with the field's unit, "45deg") or an expression in the editor's
 *    expression language (core/expr: `+=10`, `*-1`, `lerp(-400, 400, u)`, `rand(-30, 30)`). Enter or blur
 *    commits ONE edit: constants as `{ kind: 'number' }` (clamped to min/max), everything else as
 *    `{ kind: 'expr', program }` for per-item evaluation by the caller. Errors are underlined live at their
 *    character range and explained under the field; Enter keeps the text for fixing, blur and Escape restore the
 *    value. A valid expression on a single value previews its result ("= 42"). The field's active unit (`unit`,
 *    or the toggle's current unit) goes to Expr.parse, so time literals convert into it (`+=500ms` on a seconds
 *    field) and other units are refused there (CR-W1-10c-1).
 *  - Random expressions (`rand(-30, 30)`): the caller draws per item with its own generator, so no number is
 *    previewed unless the caller passes the seed it applies with (`randomSeed`): the hint then shows the seed and,
 *    on a single value, the exact result for that seed (D§4.11 "with the seed shown"; CR-W1-12-3).
 *  - Scrubbing: drag the label sideways (1 step per pixel; Shift x10 coarse, Alt x0.1 fine, Ctrl fine too for
 *    window managers that take Alt+drag), ArrowUp/Down (PageUp/Down x10; Shift coarse, Alt fine), or the mouse
 *    wheel while the field is focused (Shift coarse, Alt or Ctrl fine). Live values go to `onScrub(v, false)`,
 *    the end of the gesture to `onScrub(v, true)` (drag release, key release, 400 ms after the last wheel tick,
 *    blur); Escape during a gesture restores the start value.
 *    Without `onScrub` the field shows the live value itself and commits once at the end of the gesture.
 *  - Mixed values: "Mixed" placeholder and a hint with example expressions; label dragging and the wheel or
 *    arrows on the empty field do nothing (no common base). After typing a number, ArrowUp/Down and the wheel
 *    step from it, show the result in the field (no `onScrub`: Escape could not restore the mixed values) and
 *    commit it once with `onCommit({ kind: 'number' })` at the end of the gesture; Escape returns to the text.
 *  - Units: suffix `unit`; `unitToggle` replaces it with a button switching friendly <-> raw units.
 *  - Guided help line (`help`, hidden at Full by global.css), badges (their explanations join the input's
 *    aria-describedby; status badges are not tab stops), `helpId` for "Show me" and checker badges.
 */

import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import type { ExprApi, ExprError, ExprProgram, ExprVarSet } from '../core/expr/types';
import { useT } from '../i18n/useT';
import { BadgeList, hasStatusBadges } from './Badge';
import { useTr } from './bridge';
import { useUiCore } from './coreApis';
import { Icon } from './Icon';
import { cx } from './internal/cx';
import { pendingEditProps } from './internal/layers';
import { keepFocusOnPointerDown } from './internal/focus';
import f from './internal/form.module.css';
import { clamp, decimalsOf, formatFieldNumber, parsePlainNumber, roundToStep } from './internal/numbers';
import s from './NumberField.module.css';
import { Tooltip } from './Tooltip';
import type { NumberFieldProps } from './types';
import { m } from './ui.messages';

/** What the typed text means. */
export type NumberInput =
  | { readonly kind: 'empty' }
  | { readonly kind: 'number'; readonly value: number }
  | { readonly kind: 'expr'; readonly program: ExprProgram }
  | { readonly kind: 'error'; readonly error: ExprError };

/**
 * Interprets field text: plain numbers first (with the field's unit), then the expression language parsed for that
 * unit (`ExprApi.parse(src, vars, unit)`, CR-W1-10c-1: time literals convert into the field's unit).
 */
export function interpretNumberInput(src: string, expr: ExprApi, vars: ExprVarSet, unit?: string): NumberInput {
  if (src.trim() === '') return { kind: 'empty' };
  const plain = parsePlainNumber(src, unit);
  if (plain !== null) return { kind: 'number', value: plain };
  let parsed: ReturnType<ExprApi['parse']>;
  try {
    parsed = unit === undefined || unit === '' ? expr.parse(src, vars) : expr.parse(src, vars, unit);
  } catch {
    return { kind: 'error', error: { key: m['number.invalid'], from: 0, to: src.length } };
  }
  if (!parsed.ok) return { kind: 'error', error: parsed.error };
  const program = parsed.value;
  if (program.constant !== null && program.assign === '=' && Number.isFinite(program.constant)) {
    return { kind: 'number', value: program.constant };
  }
  return { kind: 'expr', program };
}

/**
 * Result of a program applied to one value (preview), or null when it cannot be evaluated. `seed` is the seed the
 * caller applies random programs with (one generator per application, this value drawn first).
 */
function previewResult(expr: ExprApi, program: ExprProgram, v: number, seed: number): number | null {
  try {
    const out = expr.apply(program, { v, i: 0, n: 1, u: 0 }, expr.rng(seed));
    return out !== null && Number.isFinite(out) ? out : null;
  } catch {
    return null;
  }
}

const DRAG_THRESHOLD_PX = 3;
const WHEEL_IDLE_MS = 400;

type GestureSource = 'drag' | 'keys' | 'wheel';
interface Gesture {
  /** Value restored by Escape (on a mixed field: the typed number). */
  readonly base: number;
  readonly value: number;
  readonly source: GestureSource;
  /**
   * Started from a typed number on a mixed field: live values stay in the field (no onScrub, there is no common
   * value to restore) and the end of the gesture commits once; `draft` is the text Escape brings back.
   */
  readonly mixed: { readonly draft: string } | null;
}
interface Drag {
  readonly pointerId: number;
  readonly startX: number;
  readonly lastX: number;
  readonly moved: boolean;
}

interface ModifierState {
  readonly shiftKey: boolean;
  readonly altKey: boolean;
  readonly ctrlKey?: boolean;
}

/** Step multiplier for the held modifiers: Shift coarse (x10), Alt fine (x0.1); Ctrl fine for wheel and drag. */
export function stepFactor(e: ModifierState, ctrlIsFine = false): number {
  if (e.shiftKey) return 10;
  if (e.altKey || (ctrlIsFine && e.ctrlKey === true)) return 0.1;
  return 1;
}

export interface NumberFieldExtraProps {
  readonly className?: string | undefined;
  /** Stack the label above the input (narrow panels) instead of beside it. */
  readonly stacked?: boolean | undefined;
  readonly autoFocus?: boolean | undefined;
}

export function NumberField(props: NumberFieldProps & NumberFieldExtraProps) {
  const t = useT();
  const tr = useTr();
  const { expr } = useUiCore();
  const inputId = useId();
  const labelId = useId();
  const messageId = useId();
  const helpLineId = useId();
  const badgesId = useId();
  const inputRef = useRef<HTMLInputElement>(null);

  const [draft, setDraft] = useState<string | null>(null);
  const [live, setLive] = useState<number | null>(null);
  const [focused, setFocused] = useState(false);
  const [scrollLeft, setScrollLeft] = useState(0);
  const gesture = useRef<Gesture | null>(null);
  const drag = useRef<Drag | null>(null);
  const suppressLabelClick = useRef(false);
  const wheelTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const step = props.step !== undefined && props.step > 0 ? props.step : 1;
  const precision = props.precision ?? Math.max(3, decimalsOf(step));
  const vars: ExprVarSet = props.vars ?? 'field';
  const disabled = props.disabled === true;
  const current = props.value.kind === 'value' ? props.value.value : null;
  const mixed = current === null;
  const shownNumber = live ?? current;
  const formatted = shownNumber === null ? '' : formatFieldNumber(shownNumber, precision);
  const text = draft ?? formatted;

  // The unit shown after the value is the unit typed values and expressions are in.
  const unitToggle = props.unitToggle;
  const unitLabel =
    unitToggle === undefined ? props.unit : unitToggle.mode === 'raw' ? unitToggle.raw : unitToggle.friendly;
  const otherUnit =
    unitToggle === undefined ? undefined : unitToggle.mode === 'raw' ? unitToggle.friendly : unitToggle.raw;

  const interpretation = useMemo(
    () => (draft === null ? null : interpretNumberInput(draft, expr, vars, unitLabel)),
    [draft, expr, vars, unitLabel],
  );
  const error = interpretation?.kind === 'error' ? interpretation.error : null;
  const program = interpretation?.kind === 'expr' ? interpretation.program : null;
  const random = program?.usesRand === true;
  const seed = props.randomSeed;
  const preview =
    program !== null && current !== null && (!random || seed !== undefined)
      ? previewResult(expr, program, current, seed ?? 0)
      : null;

  // ---------------------------------------------------------------- gestures (drag, keys, wheel)

  const nextValue = (base: number, steps: number, factor: number): number =>
    clamp(roundToStep(base + steps * step * factor, step * factor), props.min, props.max);

  /** The number typed into the field, or null (no draft, or the draft is not a plain number). */
  const typedNumber = (): number | null => (draft === null ? null : parsePlainNumber(draft, unitLabel));

  /**
   * Next live value of a gesture. `typed` is the number in the field when the gesture starts: on a mixed field
   * it becomes the gesture's base (see Gesture.mixed).
   */
  const gestureUpdate = (value: number, source: GestureSource, typed: number | null): void => {
    const g = gesture.current;
    if (g !== null) {
      gesture.current = { ...g, value, source };
    } else if (current !== null) {
      gesture.current = { base: current, value, source, mixed: null };
    } else if (typed !== null && draft !== null) {
      gesture.current = { base: typed, value, source, mixed: { draft } };
    } else {
      return;
    }
    setLive(value);
    if (gesture.current.mixed === null) props.onScrub?.(value, false);
  };
  const gestureEnd = (cancel = false): void => {
    const g = gesture.current;
    if (wheelTimer.current !== null) {
      clearTimeout(wheelTimer.current);
      wheelTimer.current = null;
    }
    if (g === null) return;
    gesture.current = null;
    setLive(null);
    if (g.mixed !== null) {
      // Nothing was sent yet: commit the stepped number, or bring the typed text back on Escape.
      if (cancel) setDraft(g.mixed.draft);
      else props.onCommit({ kind: 'number', value: g.value });
      return;
    }
    const value = cancel ? g.base : g.value;
    if (props.onScrub !== undefined) props.onScrub(value, true);
    else if (!cancel && value !== g.base) props.onCommit({ kind: 'number', value });
  };

  // Latest handlers for the native (non-passive) wheel listener and the Escape-while-dragging listener.
  const handlers = useRef({
    wheel: (_e: WheelEvent): void => undefined,
    cancelDrag: (): void => undefined,
  });
  useEffect(() => {
    handlers.current = {
      wheel: (e) => {
        const el = inputRef.current;
        if (el === null || document.activeElement !== el || disabled) return;
        const typed = typedNumber();
        const base = gesture.current?.value ?? typed ?? current;
        if (base === null) return;
        const delta = e.deltaY !== 0 ? e.deltaY : e.deltaX;
        if (delta === 0) return;
        e.preventDefault();
        if (draft !== null) setDraft(null);
        gestureUpdate(nextValue(base, delta < 0 ? 1 : -1, stepFactor(e, true)), 'wheel', typed);
        if (wheelTimer.current !== null) clearTimeout(wheelTimer.current);
        wheelTimer.current = setTimeout(() => {
          wheelTimer.current = null;
          gestureEnd();
        }, WHEEL_IDLE_MS);
      },
      cancelDrag: () => {
        drag.current = null;
        suppressLabelClick.current = true;
        gestureEnd(true);
      },
    };
  });
  useEffect(() => {
    const el = inputRef.current;
    if (el === null) return;
    const onWheel = (e: WheelEvent): void => {
      handlers.current.wheel(e);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      el.removeEventListener('wheel', onWheel);
      if (wheelTimer.current !== null) clearTimeout(wheelTimer.current);
    };
  }, []);

  // One stable listener (added on drag start, removed on drag end) so Escape cancels a label drag.
  const escapeListener = useRef<((e: KeyboardEvent) => void) | null>(null);
  const escapeWhileDragging = (): ((e: KeyboardEvent) => void) => {
    escapeListener.current ??= (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopPropagation();
      stopEscapeListener();
      handlers.current.cancelDrag();
    };
    return escapeListener.current;
  };
  const stopEscapeListener = (): void => {
    if (escapeListener.current !== null) window.removeEventListener('keydown', escapeListener.current, true);
  };
  useEffect(
    () => () => {
      if (escapeListener.current !== null) window.removeEventListener('keydown', escapeListener.current, true);
    },
    [],
  );

  const onLabelPointerDown = (e: ReactPointerEvent<HTMLLabelElement>): void => {
    if (e.button !== 0 || disabled || current === null) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { pointerId: e.pointerId, startX: e.clientX, lastX: e.clientX, moved: false };
    window.addEventListener('keydown', escapeWhileDragging(), true);
  };
  const onLabelPointerMove = (e: ReactPointerEvent<HTMLLabelElement>): void => {
    const d = drag.current;
    if (d?.pointerId !== e.pointerId) return;
    if (!d.moved && Math.abs(e.clientX - d.startX) < DRAG_THRESHOLD_PX) return;
    const dx = e.clientX - d.lastX;
    drag.current = { ...d, lastX: e.clientX, moved: true };
    if (dx === 0) return;
    const base = gesture.current?.value ?? current;
    if (base === null) return;
    if (draft !== null) setDraft(null);
    gestureUpdate(nextValue(base, dx, stepFactor(e, true)), 'drag', null);
  };
  const onLabelPointerEnd = (e: ReactPointerEvent<HTMLLabelElement>): void => {
    const d = drag.current;
    if (d?.pointerId !== e.pointerId) return;
    drag.current = null;
    stopEscapeListener();
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
    if (d.moved) {
      suppressLabelClick.current = true;
      gestureEnd();
    }
  };

  // ---------------------------------------------------------------- typing

  const commitDraft = (fromBlur: boolean): void => {
    if (draft === null) return;
    const r = interpretNumberInput(draft, expr, vars, unitLabel);
    switch (r.kind) {
      case 'empty':
        setDraft(null);
        return;
      case 'number': {
        const value = clamp(r.value, props.min, props.max);
        setDraft(null);
        if (current === null || value !== current) props.onCommit({ kind: 'number', value });
        return;
      }
      case 'expr':
        setDraft(null);
        props.onCommit({ kind: 'expr', program: r.program });
        return;
      case 'error':
        if (fromBlur) setDraft(null);
        return;
    }
  };

  const onKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>): void => {
    const vertical = e.key === 'ArrowUp' || e.key === 'ArrowDown';
    const page = e.key === 'PageUp' || e.key === 'PageDown';
    if ((vertical || page) && !disabled && !e.ctrlKey && !e.metaKey) {
      const typed = typedNumber();
      if (draft !== null && typed === null) return;
      const base = gesture.current?.value ?? typed ?? current;
      if (base === null) return;
      e.preventDefault();
      if (draft !== null) setDraft(null);
      const dir = e.key === 'ArrowUp' || e.key === 'PageUp' ? 1 : -1;
      gestureUpdate(nextValue(base, dir * (page ? 10 : 1), stepFactor(e)), 'keys', typed);
      return;
    }
    if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
      e.preventDefault();
      gestureEnd();
      commitDraft(false);
      e.currentTarget.select();
      return;
    }
    if (e.key === 'Escape') {
      if (gesture.current !== null) {
        e.preventDefault();
        e.stopPropagation();
        gestureEnd(true);
      } else if (draft !== null) {
        e.preventDefault();
        e.stopPropagation();
        setDraft(null);
      }
    }
  };
  const onKeyUp = (e: ReactKeyboardEvent<HTMLInputElement>): void => {
    if (gesture.current?.source !== 'keys') return;
    if (['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown'].includes(e.key)) gestureEnd();
  };

  // ---------------------------------------------------------------- rendering

  const valueText = mixed
    ? t(m.mixedValues)
    : unitLabel === undefined || unitLabel === ''
      ? formatted
      : `${formatted} ${unitLabel}`;
  const help = props.help === undefined ? null : tr(props.help);

  let message: { readonly kind: 'error' | 'info'; readonly text: string } | null = null;
  if (error !== null) message = { kind: 'error', text: tr(error) };
  else if (random) {
    const seedText = seed === undefined ? null : String(seed);
    message = {
      kind: 'info',
      text:
        seedText === null
          ? t(m['number.random'])
          : preview === null
            ? t(m['number.randomSeed'], { seed: seedText })
            : t(m['number.resultSeed'], { value: formatFieldNumber(preview, precision), seed: seedText }),
    };
  } else if (preview !== null)
    message = { kind: 'info', text: t(m['number.result'], { value: formatFieldNumber(preview, precision) }) };
  else if (focused && mixed && live === null && (draft === null || draft.trim() === ''))
    message = { kind: 'info', text: t(m['number.mixedHint']) };

  const describedBy = [
    message === null ? null : messageId,
    hasStatusBadges(props.badges) ? badgesId : null,
    help === null ? null : helpLineId,
  ]
    .filter((x) => x !== null)
    .join(' ');
  const errFrom = error === null ? 0 : Math.max(0, Math.min(text.length, error.from));
  const errTo = error === null ? 0 : Math.max(errFrom, Math.min(text.length, error.to));

  return (
    <div
      className={cx(s.field, props.stacked === true && s.stacked, disabled && s.disabled, props.className)}
      data-help-id={props.helpId}
    >
      <div className={s.labelCell}>
        <Tooltip content={t(m['number.scrubHint'])} side="top">
          <label
            id={labelId}
            htmlFor={inputId}
            className={cx(f.label, s.label, !mixed && !disabled && s.scrubbable)}
            onPointerDown={onLabelPointerDown}
            onPointerMove={onLabelPointerMove}
            onPointerUp={onLabelPointerEnd}
            onPointerCancel={onLabelPointerEnd}
            onClick={(e) => {
              // After a scrub the click must not focus the input; a plain click focuses (and selects) it.
              e.preventDefault();
              if (suppressLabelClick.current) {
                suppressLabelClick.current = false;
                return;
              }
              if (!disabled) inputRef.current?.focus();
            }}
          >
            <span className={f.labelText}>{props.label}</span>
          </label>
        </Tooltip>
        <BadgeList badges={props.badges} descriptionId={badgesId} />
      </div>
      <div className={s.controlCell}>
        <div className={cx(s.box, error !== null && s.invalid, live !== null && s.scrubbing)}>
          <div className={s.inputWrap}>
            {error !== null && (
              <div className={s.mirror} aria-hidden style={{ transform: `translateX(${String(-scrollLeft)}px)` }}>
                <span>{text.slice(0, errFrom)}</span>
                <span className={s.errorRange}>{errFrom === errTo ? ' ' : text.slice(errFrom, errTo)}</span>
                <span>{text.slice(errTo)}</span>
              </div>
            )}
            <input
              ref={inputRef}
              id={inputId}
              className={s.input}
              type="text"
              inputMode="decimal"
              role="spinbutton"
              autoComplete="off"
              spellCheck={false}
              value={text}
              placeholder={mixed ? t(m.mixed) : undefined}
              disabled={disabled}
              autoFocus={props.autoFocus}
              aria-labelledby={labelId}
              aria-valuenow={shownNumber ?? undefined}
              aria-valuemin={props.min}
              aria-valuemax={props.max}
              aria-valuetext={valueText}
              aria-invalid={error !== null ? true : undefined}
              aria-describedby={describedBy === '' ? undefined : describedBy}
              onChange={(e) => {
                gestureEnd();
                setDraft(e.target.value);
              }}
              onFocus={(e) => {
                setFocused(true);
                e.currentTarget.select();
              }}
              onBlur={() => {
                setFocused(false);
                gestureEnd();
                commitDraft(true);
              }}
              {...pendingEditProps(draft !== null || live !== null)}
              onKeyDown={onKeyDown}
              onKeyUp={onKeyUp}
              onScroll={(e) => {
                setScrollLeft(e.currentTarget.scrollLeft);
              }}
            />
          </div>
          {unitToggle !== undefined ? (
            <button
              type="button"
              className={s.unitToggle}
              disabled={disabled}
              aria-label={t(m['number.unitToggleCurrent'], { unit: unitLabel ?? '', other: otherUnit ?? '' })}
              title={t(m['number.unitToggle'], { unit: otherUnit ?? '' })}
              onMouseDown={keepFocusOnPointerDown}
              onClick={() => {
                unitToggle.onToggle();
              }}
            >
              {unitLabel}
              <Icon name="ArrowLeftRight" size={12} />
            </button>
          ) : (
            unitLabel !== undefined &&
            unitLabel !== '' && (
              <span className={s.unit} aria-hidden>
                {unitLabel}
              </span>
            )
          )}
        </div>
        {message !== null && (
          <p id={messageId} className={message.kind === 'error' ? f.error : f.message}>
            {message.kind === 'error' && <Icon name="CircleAlert" size={12} />}
            <span>{message.text}</span>
          </p>
        )}
        {help !== null && (
          <p id={helpLineId} className={f.help} data-guided-help>
            {help}
          </p>
        )}
      </div>
    </div>
  );
}
